import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import GameWrapper from '@/pages/jeux/components/GameWrapper';
import GameOverModal from '@/pages/jeux/components/GameOverModal';
import { useGame } from '@/contexts/GameContext';
import { HEALTH_TIPS } from '@/mocks/jeux-data';

type DiffLevel = 5 | 8 | 12 | 15;

type DiffType = 'colorChange' | 'addedObject' | 'missingElement' | 'patternChange' | 'sizeChange';

interface DiffDef {
  id: number;
  x: number;
  y: number;
  w: number;
  h: number;
  type: DiffType;
  title: string;
}

interface DiffPair {
  id: number;
  name: string;
  level: DiffLevel;
  differences: DiffDef[];
  imageUrl: string;
}

function generateDifferences(count: number): DiffDef[] {
  const types: DiffType[] = ['colorChange', 'addedObject', 'missingElement', 'patternChange', 'sizeChange'];
  const titles: Record<DiffType, string[]> = {
    colorChange: ['Couleur modifiée', 'Teinte différente', 'Objet recolorié', 'Nuance altérée', 'Ton changé'],
    addedObject: ['Objet ajouté', 'Élément en plus', 'Nouveau détail', 'Chose apparue', 'Détail supplémentaire'],
    missingElement: ['Objet manquant', 'Élément retiré', 'Détail absent', 'Chose disparue', 'Truc en moins'],
    patternChange: ['Motif différent', 'Texture modifiée', 'Style altéré', 'Ornement changé', 'Dessin différent'],
    sizeChange: ['Taille modifiée', 'Objet agrandi', 'Élément rétréci', 'Proportion différente', 'Format altéré'],
  };

  const used: { x: number; y: number }[] = [];
  const diffs: DiffDef[] = [];
  const minDist = 14;

  for (let i = 0; i < count; i++) {
    let x: number, y: number;
    let attempts = 0;
    do {
      x = 6 + Math.random() * 88;
      y = 6 + Math.random() * 88;
      attempts++;
    } while (attempts < 300 && used.some(u => Math.sqrt((x - u.x) ** 2 + (y - u.y) ** 2) < minDist));
    used.push({ x, y });

    const type = types[i % types.length];
    const typeTitles = titles[type];

    // Ultra petit pour être subtil
    const w = 2 + Math.random() * 3;
    const h = 2 + Math.random() * 3;

    diffs.push({
      id: i,
      x,
      y,
      w,
      h,
      type,
      title: typeTitles[i % typeTitles.length],
    });
  }

  return diffs;
}

const DIFF_PAIRS: DiffPair[] = [
  {
    id: 1, name: 'Cabinet médical',
    level: 8,
    differences: generateDifferences(8),
    imageUrl: 'https://ddfascfiaucteirbzkzd.supabase.co/storage/v1/object/public/assets/games/diff-cabinet.webp',
  },
  {
    id: 2, name: 'Salle d\'attente',
    level: 5,
    differences: generateDifferences(5),
    imageUrl: 'https://ddfascfiaucteirbzkzd.supabase.co/storage/v1/object/public/assets/games/diff-salle.webp',
  },
  {
    id: 3, name: 'Pharmacie',
    level: 12,
    differences: generateDifferences(12),
    imageUrl: 'https://ddfascfiaucteirbzkzd.supabase.co/storage/v1/object/public/assets/games/diff-pharma.webp',
  },
  {
    id: 4, name: 'Bloc opératoire',
    level: 8,
    differences: generateDifferences(8),
    imageUrl: 'https://ddfascfiaucteirbzkzd.supabase.co/storage/v1/object/public/assets/games/diff-bloc.webp',
  },
];

function DiffOverlay({ diff, found }: { diff: DiffDef; found: boolean }) {
  const baseStyle: React.CSSProperties = {
    position: 'absolute',
    left: `${diff.x}%`,
    top: `${diff.y}%`,
    width: `${diff.w}%`,
    height: `${diff.h}%`,
    transform: 'translate(-50%, -50%)',
    pointerEvents: 'none',
  };

  if (found) {
    return (
      <div
        className="absolute border border-green-500/50 rounded-sm pointer-events-none"
        style={{ ...baseStyle, backgroundColor: 'rgba(74,222,128,0.08)' }}
      >
        <div className="absolute -top-5 left-1/2 -translate-x-1/2 bg-green-500 text-white text-[10px] px-2 py-0.5 rounded-full whitespace-nowrap shadow-sm">
          {diff.title}
        </div>
      </div>
    );
  }

  switch (diff.type) {
    case 'colorChange':
      // Décalage subtil de teinte sur les pixels existants — pas de couleur ajoutée
      return (
        <div
          style={{
            ...baseStyle,
            filter: 'hue-rotate(18deg) saturate(1.25)',
            borderRadius: '2px',
          }}
        />
      );

    case 'addedObject':
      // Ombre portée subtile créant l'illusion d'un relief / objet ajouté
      return (
        <div
          style={{
            ...baseStyle,
            borderRadius: '50%',
            backgroundColor: 'rgba(0,0,0,0.08)',
            mixBlendMode: 'multiply',
            boxShadow: 'inset 0 0 3px rgba(0,0,0,0.15)',
          }}
        />
      );

    case 'missingElement':
      // Zone légèrement délavée / floutée = détail qui a disparu
      return (
        <div
          style={{
            ...baseStyle,
            filter: 'brightness(1.18) contrast(0.82) blur(0.4px)',
            borderRadius: '2px',
          }}
        />
      );

    case 'patternChange':
      // Très fines lignes diagonales quasi invisibles
      return (
        <div
          style={{
            ...baseStyle,
            backgroundImage: 'repeating-linear-gradient(135deg, rgba(0,0,0,0.06), rgba(0,0,0,0.06) 1px, transparent 1px, transparent 4px)',
            borderRadius: '2px',
            mixBlendMode: 'multiply',
            opacity: 0.65,
          }}
        />
      );

    case 'sizeChange':
      // Petit cadre pointillé presque invisible
      return (
        <div
          style={{
            ...baseStyle,
            border: '1px dotted rgba(0,0,0,0.15)',
            borderRadius: '2px',
          }}
        />
      );

    default:
      return null;
  }
}

export default function TrouveDifferences() {
  const navigate = useNavigate();
  const { startGame, endGame, gameStatus, gameScore, gameTime, gameErrors, updateScore, updateErrors } = useGame();
  const [level, setLevel] = useState<DiffLevel | null>(null);
  const [currentPair, setCurrentPair] = useState<DiffPair | null>(null);
  const [found, setFound] = useState<Set<number>>(new Set());
  const [clickFeedback, setClickFeedback] = useState<{ x: number; y: number; correct: boolean } | null>(null);
  const [showGameOver, setShowGameOver] = useState(false);
  const [started, setStarted] = useState(false);
  const [pairIndex, setPairIndex] = useState(0);

  const filteredPairs = level ? DIFF_PAIRS.filter(p => p.level === level) : [];

  const handleStart = (l: DiffLevel) => {
    setLevel(l);
    const filtered = DIFF_PAIRS.filter(p => p.level === l);
    setCurrentPair({ ...filtered[0], differences: generateDifferences(l) });
    setPairIndex(0);
    setFound(new Set());
    setClickFeedback(null);
    setShowGameOver(false);
    setStarted(true);
    startGame('trouve-differences');
  };

  const handleImageClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (!currentPair || !started || gameStatus !== 'playing') return;

    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;

    let foundDiff = -1;
    for (let i = 0; i < currentPair.differences.length; i++) {
      if (found.has(i)) continue;
      const diff = currentPair.differences[i];
      const dx = Math.abs(x - diff.x);
      const dy = Math.abs(y - diff.y);
      const hitX = dx < Math.max(diff.w / 2 + 2, 4);
      const hitY = dy < Math.max(diff.h / 2 + 2, 4);
      if (hitX && hitY) {
        foundDiff = i;
        break;
      }
    }

    if (foundDiff >= 0) {
      const newFound = new Set(found);
      newFound.add(foundDiff);
      setFound(newFound);
      updateScore(gameScore + 50);
      setClickFeedback({ x, y, correct: true });
      setTimeout(() => setClickFeedback(null), 800);

      if (newFound.size === currentPair.differences.length) {
        if (pairIndex + 1 < filteredPairs.length) {
          const nextIdx = pairIndex + 1;
          setPairIndex(nextIdx);
          const nextPair = { ...filteredPairs[nextIdx], differences: generateDifferences(level!) };
          setCurrentPair(nextPair);
          setFound(new Set());
        } else {
          const finalScore = gameScore + 200;
          updateScore(finalScore);
          endGame(finalScore, gameTime, gameErrors);
          setShowGameOver(true);
        }
      }
    } else {
      updateErrors(gameErrors + 1);
      setClickFeedback({ x, y, correct: false });
      setTimeout(() => setClickFeedback(null), 600);
    }
  }, [currentPair, found, started, gameStatus, pairIndex, filteredPairs, level, gameScore, gameTime, gameErrors, updateScore, updateErrors, endGame]);

  const handleReplay = () => { if (level) handleStart(level); };
  const handleQuit = () => { endGame(gameScore, gameTime, gameErrors); navigate('/jeux', { replace: true }); };

  if (!started) {
    return (
      <GameWrapper gameName="Trouve les Différences" onQuit={handleQuit} showPause={false}>
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="bg-background-50 rounded-3xl p-8 max-w-md w-full text-center border border-background-200/70">
            <div className="w-16 h-16 bg-accent-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-contrast-2-line text-3xl text-accent-600"></i>
            </div>
            <h2 className="text-xl font-bold text-foreground-950 mb-2">Trouve les Différences</h2>
            <p className="text-foreground-600 text-sm mb-6">
              Compare les deux images et repère les détails qui changent...
            </p>
            <p className="text-foreground-700 text-sm font-medium mb-4">Nombre de différences par image :</p>
            <div className="grid grid-cols-2 gap-3">
              {([5, 8, 12, 15] as DiffLevel[]).map((l) => (
                <button key={l} onClick={() => handleStart(l)} className="py-3 px-4 bg-secondary-100 hover:bg-secondary-200 rounded-xl text-sm font-medium text-foreground-950 transition-colors cursor-pointer whitespace-nowrap">{l} différences</button>
              ))}
            </div>
          </div>
        </div>
      </GameWrapper>
    );
  }

  if (!currentPair) return null;

  return (
    <GameWrapper gameName="Trouve les Différences" onQuit={handleQuit}>
      <div className="flex-1 flex flex-col items-center p-4">
        <div className="text-sm text-foreground-600 mb-3 flex items-center gap-4 flex-wrap justify-center">
          <span className="font-medium">{currentPair.name}</span>
          <span className="bg-secondary-100 px-3 py-1 rounded-full text-xs">
            Trouvées : <strong>{found.size}</strong>/{currentPair.differences.length}
          </span>
          <span className="text-foreground-500 text-xs">Image {pairIndex + 1}/{filteredPairs.length}</span>
        </div>

        <div className="bg-foreground-100/10 rounded-2xl p-2 mb-3 border border-background-200/70">
          <span className="text-xs text-foreground-500 flex items-center gap-1">
            <i className="ri-information-line"></i>
            Cherche les détails différents dans l&apos;image de <strong>droite</strong>. Ce sont de <strong>vraies</strong> différences de couleur, forme ou présence !
          </span>
        </div>

        <div className="flex flex-col md:flex-row gap-4 w-full max-w-5xl">
          {/* LEFT — image originale */}
          <div className="flex-1 flex flex-col items-center">
            <span className="text-xs text-foreground-500 mb-2 font-medium bg-background-100 px-3 py-1 rounded-full">Image originale</span>
            <div className="relative rounded-xl overflow-hidden border-2 border-background-200 w-full">
              <img src={currentPair.imageUrl} alt="Image originale" className="w-full h-auto" />
            </div>
          </div>

          {/* RIGHT — image avec différences subtiles en filtres CSS */}
          <div className="flex-1 flex flex-col items-center">
            <span className="text-xs text-foreground-500 mb-2 font-medium bg-accent-100 px-3 py-1 rounded-full text-accent-700">Image avec différences</span>
            <div
              className="relative rounded-xl overflow-hidden border-2 border-background-200 w-full cursor-crosshair"
              onClick={handleImageClick}
            >
              <img src={currentPair.imageUrl} alt="Image avec différences" className="w-full h-auto" />

              {/* Overlays ultra-subtils — filtres CSS uniquement, pas de couleurs */}
              {currentPair.differences.map(diff => (
                <DiffOverlay key={diff.id} diff={diff} found={found.has(diff.id)} />
              ))}

              {/* Feedback de clic */}
              {clickFeedback && (
                <div
                  className={`absolute w-10 h-10 border-2 rounded-full -translate-x-1/2 -translate-y-1/2 pointer-events-none animate-ping-once ${clickFeedback.correct ? 'border-green-500 bg-green-400/30' : 'border-red-500 bg-red-400/30'}`}
                  style={{ left: `${clickFeedback.x}%`, top: `${clickFeedback.y}%` }}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      {showGameOver && (
        <GameOverModal
          score={gameScore}
          timeElapsed={gameTime}
          errors={gameErrors}
          gameName="trouve-differences"
          message="Toutes les différences trouvées !"
          onReplay={handleReplay}
          onQuit={handleQuit}
          healthTip={HEALTH_TIPS[Math.floor(Math.random() * HEALTH_TIPS.length)]}
        />
      )}
    </GameWrapper>
  );
}